import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../utils/api";
import EnvironmentLiveTable from "../components/EnvironmentLiveTable";
import EnvironmentHistorical from "../components/EnvironmentHistorical";
import "./EnvironmentTab.css";

// Two sub-tabs, driven by the route (see the Header sub-tab bar):
//   /tools/environment            -> Live       (live sensor table)
//   /tools/environment/historical -> Historical (Analysis | Heatmap)
const EnvironmentTab = () => {
  const location = useLocation();
  const isHistoricalView = location.pathname.startsWith(
    "/tools/environment/historical",
  );

  const username = localStorage.getItem("username") || "";

  const [liveRows, setLiveRows] = useState([]);
  const [liveLoading, setLiveLoading] = useState(true);
  const [liveError, setLiveError] = useState(null);

  // Live table - polls on the same cadence as the energy live table.
  useEffect(() => {
    if (!username) {
      setLiveError("No user session found. Please log in again.");
      setLiveLoading(false);
      return;
    }
    if (isHistoricalView) return;

    let cancelled = false;
    const fetchLive = async () => {
      try {
        const res = await api.get(
          `/environment-sensors/live?username=${username}`,
        );
        if (cancelled) return;
        if (res.data && res.data.status === "success") {
          setLiveRows(res.data.data || []);
          setLiveError(null);
        } else {
          setLiveError("Failed to load live sensor data.");
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Fetch live sensors error:", err);
          setLiveError("Error connecting to server. Please try again.");
        }
      } finally {
        if (!cancelled) setLiveLoading(false);
      }
    };

    fetchLive();
    const interval = setInterval(fetchLive, 10000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [username, isHistoricalView]);

  if (!username) {
    return (
      <div className="env-container">
        <div className="env-error-card">
          <h3>⚠️ Service Interrupted</h3>
          <p>No user session found. Please log in again.</p>
        </div>
      </div>
    );
  }

  if (!isHistoricalView && !liveLoading && !liveError && liveRows.length === 0) {
    return (
      <div className="env-container">
        <div className="env-empty-card">
          <div className="env-empty-icon">🌡️</div>
          <h2>No Environment Sensors Found</h2>
          <p>
            Configure and register room temperature and humidity sensors in the
            settings page to start receiving live readings.
          </p>
          <div className="env-empty-hint">
            <strong>Hint:</strong> When registering physical meters, select brand{" "}
            <code>Irhajhus</code> and model <code>TCM-T07</code> to automatically
            classify them as environmental.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="env-container">
      {isHistoricalView ? (
        <EnvironmentHistorical username={username} />
      ) : (
        <div className="env-heatmap-section">
          <EnvironmentLiveTable
            data={liveRows}
            loading={liveLoading}
            error={liveError}
          />
        </div>
      )}
    </div>
  );
};

export default EnvironmentTab;
